import { NgStyle } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  untracked,
  viewChild,
} from '@angular/core';

import { CadsSparklineBitConfig } from './cads-sparkline-bit.config';

/** Default frame size (matches the original sparkline defaults). */
const DEFAULT_WIDTH_PX = 120;
const DEFAULT_HEIGHT_PX = 48;

/**
 * Minimal line sparkline (canvas, no axes). Stroke uses CADS semantic tokens. **Sizing:** {@link CadsSparklineBitConfig.width}
 * and **height** set the frame in CSS pixels (defaults **120×48**); matching pairs for width or max-width and height or max-height
 * keep layout from stretching the chart. Redraws when `config` changes; one initial draw runs after the view is ready.
 */
@Component({
    selector: 'cads-sparkline-bit',
    imports: [NgStyle],
    templateUrl: './cads-sparkline-bit.component.html',
    styleUrl: './cads-sparkline-bit.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CadsSparklineBitComponent {
  private readonly _hostElement = inject(ElementRef<HTMLElement>);

  readonly config = input.required<CadsSparklineBitConfig>();

  /**
   * Inline dimensions + stroke on the frame: explicit width, max-width, height, and max-height (same px pairs for each axis).
   */
  readonly frameStyle = computed<Record<string, string>>(() => {
    const c = this.config();
    const w = c.width ?? DEFAULT_WIDTH_PX;
    const h = c.height ?? DEFAULT_HEIGHT_PX;
    const key = c.color ?? 'mid-gray';
    return {
      boxSizing: 'border-box',
      width: `${w}px`,
      maxWidth: `${w}px`,
      height: `${h}px`,
      maxHeight: `${h}px`,
      minHeight: `${h}px`,
      minWidth: '0',
      '--cads-sparkline-bit-stroke': `var(--cads-color-${key})`,
    };
  });

  private readonly _frameEl = viewChild<ElementRef<HTMLElement>>('frame');
  private readonly _canvasEl = viewChild<ElementRef<HTMLCanvasElement>>('canvas');

  readonly ariaLabelText = computed(() => this.config().ariaLabel ?? 'Trend sparkline');

  constructor() {
    // First paint after the frame and canvas exist (the `effect` can run before view children are ready).
    afterNextRender(() => this._draw());

    effect(() => {
      this.config();
      untracked(() => this._draw());
    });
  }

  private _draw(): void {
    const frame = this._frameEl()?.nativeElement;
    const canvas = this._canvasEl()?.nativeElement;
    if (!frame || !canvas) {
      return;
    }

    const data = this.config().data;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    const rect = frame.getBoundingClientRect();
    const w = Math.max(1, Math.round(rect.width));
    const h = Math.max(1, Math.round(rect.height));
    const dpr = typeof window !== 'undefined' && window.devicePixelRatio ? window.devicePixelRatio : 1;

    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, h);

    if (data.length < 2) {
      return;
    }

    const strokeRaw = getComputedStyle(frame).getPropertyValue('--cads-sparkline-bit-stroke').trim();
    const stroke = strokeRaw || getComputedStyle(this._hostElement.nativeElement).color;

    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const stepX = w / (data.length - 1);
    const strokeWidth = this.config().strokeWidth ?? 2;

    ctx.beginPath();
    ctx.strokeStyle = stroke;
    ctx.lineWidth = strokeWidth;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';

    data.forEach((val, i) => {
      const x = i * stepX;
      const y = h - h * ((val - min) / range);
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    ctx.stroke();
  }
}
